/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './OCRComponent';

const OCRComponentManager: ComponentManagerFn<'OCRComponent'> = (
  platform: Platform,
): ComponentManager<'OCRComponent'> => {
  return {
    ...baseComponentManager<'OCRComponent'>(platform, 'OCRComponent'),
    type: 'OCRComponent',
    name: 'OCR Component',
    defaultValue,
    props: {
      imageUrl: {
        label: 'Image URL',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Image URL expression',
      },
      imageJsonUrl: {
        label: 'Image Json URL',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Image Json Url Expression',
      },
      selectionDisableCondition: {
        label: 'Selection Disable Condition',
        type: 'expressionOnly',
        returnType: () => 'boolean',
        helpText: 'Provide expression value to disable the selection.',
      },
      populatedValuesStoreDataSourceAlias: {
        label: 'Populated Values Store DataSource Alias',
        type: 'DataSourceAliasChooser',
        helpText: 'Give the Popoulated values store alias name',
        allowedTypes: ['CloudDataStore', 'DataStore', 'ReadOnlyDataStore'],
      },
      trainedDataStoreDataSourceAlias: {
        label: 'Trained Data Store DataSource Alias',
        type: 'DataSourceAliasChooser',
        helpText: 'Give the Trained data store alias name',
        allowedTypes: ['CloudDataStore', 'DataStore', 'ReadOnlyDataStore'],
      },
      activeAttributeCode: {
        label: 'Active Attribute Code',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Highlight the active field on the image',
      },
      loadingMessage: {
        label: 'Loading Message',
        type: 'string',
        helpText: 'Specify a message to display until it loads',
      },
      highlightKeyValueMetadata: {
        label: 'Highlight Key Value Metadata',
        type: 'boolean',
        helpText: 'Highlights the key value pair from the source',
      },
      highlightCellMetadata: {
        label: 'Highlight Cell Metadata',
        type: 'boolean',
        helpText: 'Highlights the cells from the source',
      },
      highlightAllLines: {
        label: 'Highlight All Lines',
        type: 'boolean',
        helpText: 'Highlights the lines from the source',
      },
      showPopulatedData: {
        label: 'Show Populated Data',
        type: 'boolean',
        helpText: 'Highlights the automatic populated data',
      },
      showTrainedData: {
        label: 'Show Trained Data',
        type: 'boolean',
        helpText: 'Highlights the trained data',
      },
      trainWords: {
        label: 'Train Words',
        type: 'boolean',
        helpText: 'Enable this for the words training.',
      },
      onSelectFunction: {
        label: 'On Select',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText: 'Provide a controller function to run on select of text.',
      },
      enableLabelTraining: {
        label: 'Enable Label Training',
        type: 'boolean',
        helpText:
          'Shows the popover defined at OCR component level when we mouse hover',
        category: 'Label Training',
      },
      addLabelText: {
        label: 'Add Label Text',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Provide expression value to disable the selection.',
        category: 'Label Training',
      },
      addLabelFunction: {
        label: 'Add Label Function',
        type: 'controllerFunction',
        category: 'Controllers',
        helpText: 'Provide a controller function to run on add label.',
      },
    },
    category: 'Data',
    isDataSourceAliasUsed: (item, alias) =>
      item.props.populatedValuesStoreDataSourceAlias === alias ||
      item.props.trainedDataStoreDataSourceAlias === alias,
    icon: { iconName: 'file-invoice' },
    description: `**Overview**<br/>
The OCR Component is a visual annotation tool that acts as the "human-in-the-loop" interface for document processing. It takes the output from an OCR engine (text, coordinates, and metadata) and overlays it onto the original document image.
Use this component to build Invoice Processing, Form Digitization, or Data Extraction workflows where users need to review, correct, and train the system's output.

**1. Source Data (The Inputs)**<br/>
The component requires two distinct inputs to reconstruct the document view.
* **Image URL:** The source link to the raw document image (JPG/PNG). This is the visual layer the user sees.
* **Image JSON URL:** The link to the OCR metadata file (JSON). This contains the coordinates (bounding boxes) and text content extracted by the OCR engine.
* **Purpose:** This data allows the component to "draw" interactive boxes around every recognized word or line.

**2. Data Binding & Stores**<br/>
To enable corrections and training, the component connects to two separate Data Stores. This separation allows you to track "System Guesses" vs. "Human Truth."
* **Populated Values Store:** The alias of the Data Store containing the automatically extracted values (the OCR engine's best guess).
* **Trained Data Store:** The alias of the Data Store containing the user-verified values (corrections made by the human).
* **Show Populated / Trained Data:** Boolean toggles to control which layer of data is visualized on the document. Enable both to allow users to spot discrepancies instantly.

**3. Visualization Layers**<br/>
You can control the density of information shown to the user to avoid clutter.
* **Highlight Key-Value Metadata:** Visualizes detected pairs (e.g., "Invoice Date: 12/01/2023"). Ideal for forms.
* **Highlight Cell Metadata:** Visualizes table structures (rows and columns). Ideal for line-item extraction.
* **Highlight All Lines:** Shows raw text lines. Good for debugging OCR quality or selecting large blocks of text.

**4. Interaction & Training**<br/>
This component transforms a static image into an interactive training surface.
* **Train Words:** If enabled, selecting text on the image treats the action as a "Training Event," allowing the system to learn from the correction.
* **Active Attribute Code:** An expression (e.g., \`$.currentField\`) that tells the component which field is currently being focused by the user (e.g., "Total Amount"). The component can then automatically highlight the corresponding region on the document image.
* **On Select Function:** A controller function triggered when a user selects a region. Use this to auto-fill form fields (e.g., User clicks a date on the image -> The "Date" input on the form is populated).

**5. Label Training (Advanced)**<br/>
Enable this mode to turn the component into a data labeling tool for Machine Learning workflows.
* **Enable Label Training:** Allows users to hover over text segments and assign semantic labels (e.g., "This is a Date," "This is a Vendor Name").
* **Add Label Function:** The logic executed when a new label is assigned.`,
  };
};

export default OCRComponentManager;
